import { Tooltip } from "@mui/material";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import { DatePicker } from "@mui/x-date-pickers";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { motion } from "motion/react";
import { MutableRefObject, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { TbCalendarDown, TbRefresh } from "react-icons/tb";
import { useMatches, useNavigate } from "react-router";

import FilterButton from "@/components/table/FilterButton";
import StatusFilterItem from "@/components/table/StatusFilterItem";
import { hotelPlanningTabsList } from "@/constants/hotelPlanningTabs";
import { getHotelClinicManagerTasksByDateQueryKey } from "@/requests/hotel-clinic.queries";
import {
    useHotelClinicArrivalDate,
    useHotelClinicArrivalDateActions,
} from "@/stores/use-clinic-arrival-unassigned-passengers-table";
import {
    useHotelPlanningDepartureActions,
    useHotelPlanningDepartureChanges,
    useHotelPlanningDepartureDate,
    useHotelPlanningDepartureIsPastDate,
} from "@/stores/use-hotel-planning-departure-table";

import HotelDepartureChangesButton from "./HotelDepartureChangesButton";

const CLINIC_ARRIVAL_PATH = "/hastane-otel/";

function HotelPlanningTabs({ apiRef }: { apiRef: MutableRefObject<GridApiPremium> }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const activeRoute = useMatches().at(-1);
    const navigate = useNavigate();
    const clinicArrivalDate = useHotelClinicArrivalDate();
    const { setDate: setClinicArrivalDate } = useHotelClinicArrivalDateActions();
    const departureDate = useHotelPlanningDepartureDate();
    const isDeparturePastDate = useHotelPlanningDepartureIsPastDate();
    const departureChanges = useHotelPlanningDepartureChanges();
    const { setDate: setDepartureDate } = useHotelPlanningDepartureActions();
    // Switching day drops unsaved edits — block it until they are saved or cancelled.
    const hasDepartureChanges = Object.keys(departureChanges).length > 0;

    const refetchClinicArrival = useCallback(() => {
        const iso = clinicArrivalDate.toISODate();
        if (!iso) return;
        void queryClient.refetchQueries({ queryKey: getHotelClinicManagerTasksByDateQueryKey(iso) });
    }, [clinicArrivalDate, queryClient]);

    const activeIndex = useMemo(
        () => hotelPlanningTabsList.findIndex((item) => activeRoute && item.value === activeRoute.pathname),
        [activeRoute]
    );
    const activeStyle = useMemo(
        () =>
            activeIndex !== -1
                ? {
                      translateX: `${activeIndex * 100}%`,
                      backgroundColor: hotelPlanningTabsList[activeIndex].hover,
                  }
                : false,
        [activeIndex]
    );

    const handleUpdate = useCallback(
        (pathname: string, active?: boolean) => {
            if (active) return;
            navigate(pathname, { replace: true });
        },
        [navigate]
    );

    const isClinicArrivalTab = activeRoute?.pathname === CLINIC_ARRIVAL_PATH;

    return (
        <div className="flex justify-between items-center">
            <div className=" relative py-1 grid grid-cols-2 min-w-[300px] md:min-w-[450px] bg-gray-100/80  divide-x divide-gray-300    rounded">
                {hotelPlanningTabsList.map((item) => (
                    <StatusFilterItem
                        key={item.tooltip.title}
                        {...item}
                        active={false}
                        count={null}
                        handleUpdate={handleUpdate}
                    >
                        {item.label}
                    </StatusFilterItem>
                ))}
                {activeStyle && (
                    <motion.div
                        layoutId="hotel-planning-tabs-active-style"
                        id="hotel-planning-tabs-active-style"
                        animate={activeStyle}
                        transition={{ type: "tween" }}
                        className="  rounded-none absolute   !self-center  !h-[calc(100%-8px)]     !w-1/2 "
                    ></motion.div>
                )}
            </div>
            <div className="flex min-w-0 shrink-0 flex-wrap items-center justify-end gap-2">
                {isClinicArrivalTab ? (
                    <>
                        <FilterButton
                            handleClick={() => refetchClinicArrival()}
                            icon={TbRefresh}
                            tooltip={t("tooltips.refreshTable")}
                        />
                        <DatePicker
                            className="!w-48"
                            value={clinicArrivalDate}
                            onChange={(d) => {
                                if (d) setClinicArrivalDate(d);
                            }}
                        />
                    </>
                ) : (
                    <>
                        {!isDeparturePastDate && <HotelDepartureChangesButton apiRef={apiRef} />}
                        {isDeparturePastDate && (
                            <FilterButton
                                handleClick={() => setDepartureDate(DateTime.now())}
                                icon={TbCalendarDown}
                                tooltip="Bugüne dön"
                            />
                        )}
                        <Tooltip
                            title={
                                hasDepartureChanges
                                    ? "Tarihi değiştirmeden önce değişiklikleri kaydedin veya iptal edin"
                                    : ""
                            }
                        >
                            <span>
                                <DatePicker
                                    className="!w-48"
                                    value={departureDate}
                                    disableFuture
                                    disabled={hasDepartureChanges}
                                    onChange={(d) => {
                                        if (d?.isValid) setDepartureDate(d);
                                    }}
                                />
                            </span>
                        </Tooltip>
                    </>
                )}
            </div>
        </div>
    );
}

export default HotelPlanningTabs;
